import { ArrowRight, Star, ShieldCheck } from "lucide-react";
import { SITE, STATS, GOOGLE_REVIEWS } from "@/data/site";

export default function Hero() {
  const goto = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end md:justify-center overflow-hidden grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1669606072600-1a62d7f24873?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxibGFjayUyMHNwb3J0cyUyMGNhciUyMHNsZWVrJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc3MjY0ODM0fDA&ixlib=rb-4.1.0&q=85"
          alt="Black sports car"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        <div className="absolute inset-0 hero-glow" />
      </div>

      {/* Top eyebrow line (desktop) */}
      <div className="hidden md:block absolute top-28 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center gap-4">
          <div className="w-16 shimmer-line h-px" />
          <p className="eyebrow">
            Pukekohe · Auckland · Mobile Detailing Studio
          </p>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 pb-12 md:pt-32 md:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-end">
        <div className="lg:col-span-8">
          <p className="eyebrow md:hidden mb-6 tracking-[0.24em]">
            Pukekohe · Auckland · Mobile Detailing
          </p>
          <h1
            data-testid="hero-headline"
            className="font-display text-white font-light leading-[0.95] text-5xl md:text-7xl lg:text-[5.5rem] tracking-tighter"
          >
            The Pinnacle <br />
            of <span className="gold-text font-medium italic">Automotive</span>{" "}
            <br />
            <span className="font-serif italic text-white/95">Protection.</span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-8 text-white/65 max-w-xl text-base md:text-lg leading-relaxed"
          >
            Family-owned, detail-obsessed. From multi-stage paint correction to
            decade-grade ceramic coatings — every prized motor entrusted to{" "}
            {SITE.shortName} leaves a measurable cut above.
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
            <button
              data-testid="hero-cta-book"
              onClick={() => goto("quote")}
              className="btn-gold px-8 py-4 text-xs flex items-center justify-center gap-3 group"
            >
              Get an Instant Quote
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => goto("services")}
              className="btn-outline-gold px-8 py-4 text-xs"
            >
              Explore Services
            </button>
          </div>

          {/* Trust line */}
          <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/55 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]"
                  />
                ))}
              </div>
              <a
                href={GOOGLE_REVIEWS.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-google-link"
                className="hover:text-[#D4AF37] transition-colors underline-offset-4 hover:underline"
              >
                Rated {GOOGLE_REVIEWS.rating} on Google
              </a>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              <span>Premium products only</span>
            </div>
          </div>
        </div>

        {/* Vertical card / quick info */}
        <div className="lg:col-span-4">
          <div className="glass-card p-6 md:p-7 relative">
            <p className="eyebrow">House Manifesto</p>
            <p className="mt-4 font-serif italic text-xl md:text-2xl leading-snug text-white">
              “Detailing is what I love to do. Restoring your vehicle is my
              mission — what I live and breathe, 24/7, 365.”
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-px bg-[#D4AF37]" />
              <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                Warren Edgecombe
              </p>
            </div>
            <p className="text-white/50 text-xs mt-1">Owner / Detailer</p>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative md:absolute md:bottom-0 left-0 right-0 border-t border-white/5 bg-black/60 backdrop-blur-md z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 md:divide-x divide-white/5">
          {STATS.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="py-5 md:py-6 md:px-4 md:first:pl-0"
            >
              <div className="font-display text-2xl md:text-4xl text-white font-light">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/45 mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
